import { useState, useEffect, useRef } from 'react';
import { supabase } from '@/lib/supabase';
import { useScrollAnimation, useStaggerAnimation } from '@/hooks/useScrollAnimation';

interface AiProduct {
  id: number;
  name: string;
  subtitle: string;
  description: string;
  icon: string;
  codename: string;
  tag: string;
  features: string[];
  image_url: string;
  tag_color: string;
  accent: string;
  icon_color: string;
  icon_bg: string;
  sort_order: number;
}

const FALLBACK_PRODUCTS: AiProduct[] = [
  {
    id: 1,
    name: 'DRAGON INNOVATE FUTURE-Vision',
    codename: 'DRAGON INNOVATE FUTURE-Vision',
    subtitle: '全新视觉深度思考模型',
    tag: 'NEW',
    tag_color: 'bg-emerald-500',
    description: '融合多模态视觉感知与深度推理能力，支持图像理解、场景分析、视觉问答等复杂任务，实现从像素到语义的端到端智能处理。',
    features: ['多模态视觉理解', '深度场景推理', '图文联合分析', '高精度目标识别'],
    image_url: 'https://public.readdy.ai/ai/img_res/d19a43affd4d04757ad8bdbcce5d3cc9.jpg',
    accent: 'from-teal-500 to-cyan-500',
    icon_bg: 'bg-teal-50',
    icon: 'ri-eye-line',
    icon_color: 'text-teal-600',
    sort_order: 1,
  },
  {
    id: 2,
    name: 'DRAGON INNOVATE FUTURE-Melody',
    codename: 'DRAGON INNOVATE FUTURE-Melody',
    subtitle: '语音与音乐生成模型',
    tag: 'AUDIO',
    tag_color: 'bg-violet-500',
    description: '集语音合成、声音克隆与AI音乐创作于一体，支持多语言、多风格的高保真语音输出与原创音乐生成，赋能内容创作与智能交互。',
    features: ['高保真语音合成', '声音克隆定制', 'AI音乐创作', '多语言支持'],
    image_url: 'https://readdy.ai/api/search-image?query=Abstract%20AI%20music%20and%20voice%20generation%20visualization%20with%20sound%20waves%20and%20musical%20notes%20flowing%20through%20digital%20space%2C%20warm%20orange%20and%20gold%20gradient%20waveforms%20on%20dark%20background%2C%20futuristic%20audio%20technology%20aesthetic&width=600&height=400&seq=aiprod2&orientation=landscape',
    accent: 'from-violet-500 to-purple-500',
    icon_bg: 'bg-violet-50',
    icon: 'ri-music-2-line',
    icon_color: 'text-violet-600',
    sort_order: 2,
  },
  {
    id: 3,
    name: 'DRAGON INNOVATE FUTURE-Vgen 1.5',
    codename: 'DRAGON INNOVATE FUTURE-Vgen 1.5',
    subtitle: '视频生成模型',
    tag: 'V1.5',
    tag_color: 'bg-orange-500',
    description: '基于扩散模型与时序建模技术，支持文本到视频、图像到视频的高质量生成，具备连贯动态、细节丰富、风格可控等核心能力。',
    features: ['文本驱动视频生成', '图像动态化', '风格迁移控制', '高清时序输出'],
    image_url: 'https://readdy.ai/api/search-image?query=AI%20video%20generation%20model%20concept%20with%20film%20frames%20and%20digital%20video%20streams%20flowing%20through%20futuristic%20interface%2C%20cinematic%20orange%20and%20red%20gradient%20light%20effects%20on%20dark%20background%2C%20advanced%20video%20synthesis%20technology%20visualization&width=600&height=400&seq=aiprod3&orientation=landscape',
    accent: 'from-orange-500 to-red-500',
    icon_bg: 'bg-orange-50',
    icon: 'ri-video-line',
    icon_color: 'text-orange-600',
    sort_order: 3,
  },
  {
    id: 4,
    name: 'DRAGON INNOVATE FUTURE-Seed 1.6',
    codename: 'DRAGON INNOVATE FUTURE-Seed 1.6',
    subtitle: '深度思考模型',
    tag: 'V1.6',
    tag_color: 'bg-teal-600',
    description: '新一代大语言模型，具备强化推理、链式思考与复杂问题分解能力，在代码生成、数学推导、逻辑分析等高难度任务中表现卓越。',
    features: ['链式深度推理', '复杂逻辑分析', '代码智能生成', '数学推导能力'],
    image_url: 'https://readdy.ai/api/search-image?query=Advanced%20deep%20thinking%20AI%20model%20with%20glowing%20brain%20neural%20network%20and%20complex%20reasoning%20pathways%20visualization%2C%20sophisticated%20teal%20and%20green%20digital%20intelligence%20on%20dark%20background%2C%20representing%20powerful%20language%20model%20cognition&width=600&height=400&seq=aiprod4&orientation=landscape',
    accent: 'from-teal-600 to-green-500',
    icon_bg: 'bg-teal-50',
    icon: 'ri-brain-line',
    icon_color: 'text-teal-700',
    sort_order: 4,
  },
  {
    id: 5,
    name: 'DRAGON INNOVATE FUTURE-Voice E2E',
    codename: 'DRAGON INNOVATE FUTURE-Voice E2E',
    subtitle: '实时语音大模型·端到端框架',
    tag: 'E2E',
    tag_color: 'bg-sky-500',
    description: '端到端实时语音交互框架，消除传统ASR+NLP+TTS的级联延迟，实现毫秒级语音理解与响应，支持自然对话、情感感知与多轮交互。',
    features: ['端到端实时处理', '毫秒级响应延迟', '情感感知交互', '多轮对话管理'],
    image_url: 'https://readdy.ai/api/search-image?query=Real-time%20voice%20AI%20end-to-end%20framework%20with%20dynamic%20sound%20wave%20visualization%20and%20instant%20speech%20processing%20interface%2C%20electric%20blue%20and%20cyan%20flowing%20audio%20signals%20on%20dark%20background%2C%20representing%20ultra-low%20latency%20voice%20interaction%20technology&width=600&height=400&seq=aiprod5&orientation=landscape',
    accent: 'from-sky-500 to-blue-500',
    icon_bg: 'bg-sky-50',
    icon: 'ri-mic-line',
    icon_color: 'text-sky-600',
    sort_order: 5,
  },
];

export default function AIProducts() {
  const [products, setProducts] = useState<AiProduct[]>(FALLBACK_PRODUCTS);
  const [activeId, setActiveId] = useState(1);
  const [displayId, setDisplayId] = useState(1);
  const [transitioning, setTransitioning] = useState(false);
  const transitionTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const activeProduct = products.find((p) => p.id === displayId) || products[0];

  const { ref: headerRef, isVisible: headerVisible } = useScrollAnimation();
  const { ref: listRef, visibleItems: listVisible } = useStaggerAnimation(5, { threshold: 0.05, rootMargin: '0px 0px -30px 0px' });
  const { ref: detailRef, isVisible: detailVisible } = useScrollAnimation({ threshold: 0.05, rootMargin: '0px 0px -30px 0px' });
  const { ref: statsRef, visibleItems: statsVisible } = useStaggerAnimation(4, { threshold: 0.1 });

  useEffect(() => {
    const fetchProducts = async () => {
      const { data, error } = await supabase
        .from('cms_ai_products')
        .select('*')
        .eq('is_published', true)
        .order('sort_order', { ascending: true });

      if (!error && data && data.length > 0) {
        setProducts(data as AiProduct[]);
        setActiveId(data[0].id);
        setDisplayId(data[0].id);
      }
    };
    fetchProducts();
  }, []);

  const handleSelect = (id: number) => {
    if (id === activeId || transitioning) return;
    setTransitioning(true);
    setActiveId(id);
    if (transitionTimer.current) clearTimeout(transitionTimer.current);
    transitionTimer.current = setTimeout(() => {
      setDisplayId(id);
      setTransitioning(false);
    }, 220);
  };

  useEffect(() => {
    return () => {
      if (transitionTimer.current) clearTimeout(transitionTimer.current);
    };
  }, []);

  return (
    <section id="ai-products" className="py-12 md:py-20 lg:py-24 bg-gray-950 overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div
          ref={headerRef}
          className="mb-7 md:mb-12 lg:mb-14"
          style={{
            transition: 'opacity 0.7s ease, transform 0.7s ease',
            opacity: headerVisible ? 1 : 0,
            transform: headerVisible ? 'translateY(0)' : 'translateY(40px)',
          }}
        >
          <span className="text-xs font-semibold tracking-widest text-teal-400 uppercase">
            AI PRODUCT SERIES
          </span>
          <div className="mt-3 md:mt-4 flex flex-col lg:flex-row lg:items-end lg:justify-between gap-2 md:gap-4">
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold text-white leading-tight">
              龙创未来 <span className="text-transparent bg-clip-text bg-gradient-to-r from-teal-400 to-cyan-400">AI 产品矩阵</span>
            </h2>
            <p className="text-gray-400 text-xs sm:text-sm md:text-base max-w-md leading-relaxed">
              自主研发的系列AI大模型产品，覆盖视觉、语音、视频、推理与实时交互全场景，驱动智能应用落地。
            </p>
          </div>
        </div>

        {/* 手机端：竖向堆叠布局 */}
        <div className="lg:hidden flex flex-col gap-4">
          <div className="grid grid-cols-2 gap-3">
            {products.map((product) => (
              <button
                key={product.id}
                onClick={() => handleSelect(product.id)}
                className={`relative rounded-2xl overflow-hidden cursor-pointer text-left transition-all duration-300 ${
                  activeId === product.id
                    ? 'ring-2 ring-teal-400 shadow-lg shadow-teal-500/20 scale-[1.03]'
                    : 'ring-1 ring-white/10 hover:ring-white/30 hover:scale-[1.01]'
                }`}
                style={{ height: '120px' }}
              >
                <img
                  src={product.image_url}
                  alt={product.subtitle}
                  className="absolute inset-0 w-full h-full object-cover object-top"
                />
                <div className={`absolute inset-0 transition-all duration-300 ${
                  activeId === product.id
                    ? 'bg-gradient-to-t from-black/90 via-black/50 to-black/20'
                    : 'bg-gradient-to-t from-black/85 via-black/60 to-black/30'
                }`} />
                <div className={`absolute top-2 right-2 w-5 h-5 flex items-center justify-center bg-teal-400 rounded-full transition-all duration-300 ${
                  activeId === product.id ? 'opacity-100 scale-100' : 'opacity-0 scale-50'
                }`}>
                  <i className="ri-check-line text-black text-xs"></i>
                </div>
                <div className="absolute inset-0 p-3 flex flex-col justify-between">
                  <span className={`self-start text-[10px] font-bold px-2 py-0.5 rounded-full text-white ${product.tag_color}`}>
                    {product.tag}
                  </span>
                  <div>
                    <div className={`text-[10px] font-mono font-semibold bg-gradient-to-r ${product.accent} bg-clip-text text-transparent mb-0.5 line-clamp-1`}>
                      {product.codename.split(' ').slice(-1)[0]}
                    </div>
                    <p className="text-white text-xs font-semibold leading-tight line-clamp-2">
                      {product.subtitle}
                    </p>
                  </div>
                </div>
              </button>
            ))}
          </div>

          <div>
            <div
              className="rounded-2xl overflow-hidden bg-white/5 border border-white/10"
              style={{
                transition: 'opacity 0.22s ease, transform 0.22s ease',
                opacity: transitioning ? 0 : 1,
                transform: transitioning ? 'translateY(12px) scale(0.98)' : 'translateY(0) scale(1)',
              }}
            >
              <div className="relative h-44 overflow-hidden">
                <img
                  src={activeProduct.image_url}
                  alt={activeProduct.subtitle}
                  className="w-full h-full object-cover object-top"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-gray-950/90 via-gray-950/30 to-transparent"></div>
                <div className="absolute top-3 left-3">
                  <span className={`text-xs font-bold px-1.5 py-0.5 rounded-full text-white whitespace-nowrap ${activeProduct.tag_color}`}>
                    {activeProduct.tag}
                  </span>
                </div>
                <div className="absolute bottom-3 left-4 right-4">
                  <span className={`text-[10px] font-mono font-semibold bg-gradient-to-r ${activeProduct.accent} bg-clip-text text-transparent block mb-0.5`}>
                    {activeProduct.codename}
                  </span>
                  <h3 className="text-lg font-bold text-white leading-tight">{activeProduct.subtitle}</h3>
                </div>
              </div>
              <div className="p-4">
                <p className="text-gray-400 text-sm leading-relaxed mb-4">{activeProduct.description}</p>
                <div className="flex flex-wrap gap-2 mb-4">
                  {(activeProduct.features || []).map((feature, idx) => (
                    <div key={idx} className="flex items-center gap-1.5 bg-white/5 rounded-lg px-2.5 py-1.5">
                      <div className="w-3.5 h-3.5 flex items-center justify-center flex-shrink-0">
                        <i className="ri-checkbox-circle-fill text-teal-400 text-xs"></i>
                      </div>
                      <span className="text-gray-300 text-xs">{feature}</span>
                    </div>
                  ))}
                </div>
                <div className="flex gap-2">
                  <a
                    href="#contact"
                    className={`flex-1 py-3.5 rounded-xl text-base font-semibold text-white text-center bg-gradient-to-r ${activeProduct.accent} hover:opacity-90 transition-opacity whitespace-nowrap cursor-pointer`}
                  >
                    申请体验
                  </a>
                  <a
                    href="#contact"
                    className="flex-1 py-3.5 rounded-xl text-base font-semibold text-gray-300 text-center border border-white/20 hover:bg-white/10 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    了解详情
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* 桌面端：左右两栏布局 */}
        <div className="hidden lg:grid lg:grid-cols-5 gap-6">
          {/* Left: Product Tabs */}
          <div ref={listRef} className="lg:col-span-2">
            <div className="flex flex-col gap-2">
              {products.map((product, idx) => (
                <button
                  key={product.id}
                  onClick={() => handleSelect(product.id)}
                  className={`group text-left px-6 py-5 rounded-2xl transition-all duration-300 cursor-pointer relative overflow-hidden ${
                    activeId === product.id
                      ? 'bg-white/10 shadow-lg shadow-teal-500/5 scale-[1.02]'
                      : 'bg-transparent hover:bg-white/5 hover:scale-[1.01]'
                  }`}
                  style={{
                    transition: `opacity 0.5s ease ${idx * 100}ms, transform 0.5s ease ${idx * 100}ms, background 0.3s ease, box-shadow 0.3s ease`,
                    opacity: listVisible[idx] ? 1 : 0,
                    transform: listVisible[idx]
                      ? activeId === product.id ? 'translateX(0) scale(1.02)' : 'translateX(0) scale(1)'
                      : 'translateX(-32px) scale(1)',
                  }}
                >
                  <div className={`absolute left-0 top-1/2 -translate-y-1/2 w-[3px] rounded-full transition-all duration-300 ${
                    activeId === product.id
                      ? `h-10 bg-gradient-to-b ${product.accent}`
                      : 'h-0 bg-transparent'
                  }`}></div>
                  <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      <div className={`font-mono text-sm tracking-wider font-semibold mb-1.5 transition-colors duration-300 ${
                        activeId === product.id ? 'text-teal-400' : 'text-gray-600'
                      }`}>
                        {product.codename}
                      </div>
                      <div className={`text-lg font-medium tracking-wide transition-colors duration-300 ${
                        activeId === product.id ? 'text-white' : 'text-gray-400 group-hover:text-gray-300'
                      }`}>
                        {product.subtitle}
                      </div>
                    </div>
                    <div className={`flex items-center gap-2 flex-shrink-0 transition-all duration-300 ${
                      activeId === product.id ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-2'
                    }`}>
                      <span className={`text-xs font-bold px-2 py-0.5 rounded-full text-white ${product.tag_color}`}>
                        {product.tag}
                      </span>
                      <div className="w-5 h-5 flex items-center justify-center">
                        <i className="ri-arrow-right-s-line text-teal-400 text-base"></i>
                      </div>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Right: Product Detail */}
          <div
            ref={detailRef}
            className="lg:col-span-3"
            style={{
              transition: 'opacity 0.7s ease 200ms, transform 0.7s ease 200ms',
              opacity: detailVisible ? 1 : 0,
              transform: detailVisible ? 'translateX(0)' : 'translateX(40px)',
            }}
          >
            <div
              className="rounded-3xl overflow-hidden bg-white/5 border border-white/10 h-full flex flex-col"
              style={{
                transition: 'opacity 0.22s ease, transform 0.22s ease',
                opacity: transitioning ? 0 : 1,
                transform: transitioning ? 'translateY(16px) scale(0.97)' : 'translateY(0) scale(1)',
              }}
            >
              <div className="relative h-56 overflow-hidden flex-shrink-0">
                <img
                  src={activeProduct.image_url}
                  alt={activeProduct.subtitle}
                  className="w-full h-full object-cover object-top"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-gray-950/90 via-gray-950/30 to-transparent"></div>
                <div className={`absolute inset-0 bg-gradient-to-br ${activeProduct.accent} opacity-10 mix-blend-screen transition-all duration-500`}></div>
                <div className="absolute top-5 left-5">
                  <span className={`text-sm font-bold px-3 py-1.5 rounded-full text-white ${activeProduct.tag_color}`}>
                    {activeProduct.tag}
                  </span>
                </div>
              </div>
              <div className="p-6 lg:p-8 flex flex-col flex-1">
                <div className="mb-2">
                  <span className={`text-xs font-mono font-semibold bg-gradient-to-r ${activeProduct.accent} bg-clip-text text-transparent`}>
                    {activeProduct.codename}
                  </span>
                </div>
                <h3 className="text-3xl lg:text-4xl font-bold text-white mb-4">{activeProduct.subtitle}</h3>
                <p className="text-gray-400 text-base leading-relaxed lg:text-lg mb-6">{activeProduct.description}</p>
                <div className="grid grid-cols-2 gap-3 mb-8">
                  {(activeProduct.features || []).map((feature, idx) => (
                    <div key={idx} className="flex items-center gap-2">
                      <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                        <i className="ri-checkbox-circle-fill text-teal-400 text-base"></i>
                      </div>
                      <span className="text-gray-300 text-sm md:text-base">{feature}</span>
                    </div>
                  ))}
                </div>
                <div className="flex gap-3 mt-auto">
                  <a
                    href="#contact"
                    className={`flex-1 py-3.5 rounded-xl text-base font-semibold text-white text-center bg-gradient-to-r ${activeProduct.accent} hover:opacity-90 transition-opacity whitespace-nowrap cursor-pointer`}
                  >
                    申请体验
                  </a>
                  <a
                    href="#contact"
                    className="flex-1 py-3.5 rounded-xl text-base font-semibold text-gray-300 text-center border border-white/20 hover:bg-white/10 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    了解详情
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Stats */}
        <div ref={statsRef} className="mt-6 md:mt-10 lg:mt-12 grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-6">
          {[
            { value: '5+', label: '自研AI大模型', icon: 'ri-cpu-line' },
            { value: '10+', label: '行业应用场景', icon: 'ri-apps-line' },
            { value: '100+', label: '企业客户部署', icon: 'ri-building-line' },
            { value: '99.9%', label: '服务可用率', icon: 'ri-shield-check-line' },
          ].map((stat, idx) => (
            <div
              key={idx}
              className="bg-white/5 border border-white/10 rounded-xl md:rounded-2xl p-3.5 md:p-6 text-center"
              style={{
                transition: `opacity 0.6s ease ${idx * 100}ms, transform 0.6s ease ${idx * 100}ms`,
                opacity: statsVisible[idx] ? 1 : 0,
                transform: statsVisible[idx] ? 'translateY(0) scale(1)' : 'translateY(32px) scale(0.95)',
              }}
            >
              <div className="w-7 h-7 md:w-10 md:h-10 flex items-center justify-center mx-auto mb-1.5 md:mb-3">
                <i className={`${stat.icon} text-lg md:text-2xl text-teal-400`}></i>
              </div>
              <div className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold text-white mb-0.5 md:mb-1">{stat.value}</div>
              <div className="text-gray-500 text-xs md:text-sm lg:text-base">{stat.label}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
